import {
    Grid,
    Alert,
    Box,
    TextField,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableRow,
    Paper,
    TableBody,
    Select,
    MenuItem,
    CircularProgress,
    FormControl,
    Typography
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useState, useEffect } from 'react'
import moment from 'moment';
import { getBiocharLabSamples, postBiocharLabSamples } from '../../../../../actions/rof-burns/rof-actions';

export default function RofBiocharLabSamples({ biocharLabSamples, selectedProject }) {
    const [biocharLabSamplesState, setBiocharLabSamplesState] = useState([])
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    // const [loadingType, setLoadingType] = useState('dataSubmissionz')

    // Populate editable state from props
    useEffect(() => {
        if (biocharLabSamples?.data) {
            setBiocharLabSamplesState(
                biocharLabSamples.data.map((row, idx) => ({
                    ...row,
                    rowId: `${row.batchId}_${row.kilnId}_${idx}`,
                    // Ensure all fields exist for editing
                    sample_number: row.sample_number || '',
                    sample_date: row.sample_date || '',
                    sample_type: row.sample_type || '',
                    h_corg: row.h_corg ?? '',
                    organic_carbon_content: row.organic_carbon_content ?? '',
                    lab_report: row.lab_report || null,
                }))
            );
        }
    }, [biocharLabSamples]);

    // Handle input changes
    const handleInputChange = (rowId, field, value) => {
        setBiocharLabSamplesState(prev =>
            prev.map(row =>
                row.rowId === rowId ? { ...row, [field]: value } : row
            )
        );
    };

    // Handle date selection
    const handleDateSelection = (date, rowId) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        handleInputChange(rowId, 'sample_date', formattedDate);
    };

    // Handle file input
    const handleSourceDoc = (e, rowId) => {
        const file = e.target.files[0];
        handleInputChange(rowId, 'lab_report', file);
    };

    const sampleTypes = [
        { id: 0, name: 'IPS' },
        { id: 1, name: 'RS' }
    ]

    // Required fields for a "complete" row
    const REQUIRED = [
        "sample_date",
        "sample_type",
        "h_corg",
        "organic_carbon_content",
    ];

    // Simple validators
    const isValidDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
    const isValidNumber = (v) => v !== "" && v !== null && v !== undefined && isFinite(Number(v));
    const isValidSampleType = (v) => v === "IPS" || v === "RS";

    // Helpers to inspect a row
    const isEmptyRow = (row) => REQUIRED.every((k) => !row[k]); // all empty
    const isCompleteRow = (row) => REQUIRED.every((k) => !!row[k]); // all filled

    // Stronger per-row validation (only called if isCompleteRow is true)
    const validateCompleteRow = (row) => {
        if (!isValidDate(row.sample_date)) return "Sample Date must be YYYY-MM-DD.";
        if (!isValidSampleType(row.sample_type)) return "Sample type must be 'IPS' or 'RS'.";
        if (!isValidNumber(row.h_corg)) return "H:Corg must be a number.";
        if (!isValidNumber(row.organic_carbon_content)) return "Organic C Content must be a number.";
        return null;
    };

    // Derive sample_number from batchId if missing
    const deriveSampleNumber = (batchId) =>
        (batchId || "").slice(-3) || undefined;

    const handleSubmitData = async () => {
        setLoading(true);
        setError(false);
        setErrorMsg && setErrorMsg("");

        try {
            const emptyRows = [];
            const partialRows = [];
            const completeRows = [];

            biocharLabSamplesState.forEach((row, idx) => {
                if (isEmptyRow(row)) {
                    emptyRows.push(row);
                } else if (isCompleteRow(row)) {
                    completeRows.push({ index: idx, row });
                } else {
                    partialRows.push({ index: idx, row });
                }
            });

            // Stop if there are partial rows
            if (partialRows.length > 0) {
                const first = partialRows[0];
                setError(true);
                setErrorMsg &&
                    setErrorMsg(
                        `Row ${first.index + 1} is partially completed. Please fill all required fields or clear the row.`
                    );
                setLoading(false);
                return;
            }

            // Validate each complete row
            for (const { index, row } of completeRows) {
                const msg = validateCompleteRow(row);
                if (msg) {
                    setError(true);
                    setErrorMsg && setErrorMsg(`Row ${index + 1}: ${msg}`);
                    setLoading(false);
                    return;
                }
            }

            // Nothing to submit
            if (completeRows.length === 0) {
                setLoading(false);
                return;
            }

            const payload = [];
            completeRows.forEach(({ row }, idx) => {
                const entry = {
                    batchId: row.batchId,
                    kilnId: String(row.kilnId),
                    sample_number: row.sample_number || deriveSampleNumber(row.batchId),
                    sample_date: row.sample_date,
                    sample_type: row.sample_type,
                    h_corg: String(row.h_corg),
                    organic_carbon_content: String(row.organic_carbon_content),
                    lab_report: null,
                };
                if (row.lab_report && typeof row.lab_report !== "string") {
                    entry.lab_report = `lab_report_${idx}`;
                }
                payload.push(entry);
            });

            const formData = new FormData();
            formData.append("data", JSON.stringify(payload));
            completeRows.forEach(({ row }, idx) => {
                if (row.lab_report && typeof row.lab_report !== "string") {
                    formData.append(`lab_report_${idx}`, row.lab_report);
                }
            });

            dispatch(postBiocharLabSamples(formData, selectedProject.id)).then(() => {
                dispatch(getBiocharLabSamples(selectedProject.id));
            })

        } catch (e) {
            setError(true);
            setErrorMsg && setErrorMsg("Failed to submit data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // const handleDownload = async () => {
    //     try {
    //         setLoading(true);
    //         setLoadingType('excelDownload');
    //         // dispatch(downloadBiocharLabSamples(projectId)).then((response) => {

    //         //     // Create a downloadable link for the Excel file
    //         //     const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    //         //     const link = document.createElement('a');
    //         //     link.href = url;
    //         //     link.setAttribute('download', `biochar_lab_samples.xlsx`); // Set the file name
    //         //     document.body.appendChild(link);
    //         //     link.click();
    //         //     link.remove();
    //         // })

    //     } catch (err) {
    //         setLoading(false)
    //         console.error('Error downloading Biochar Lab Samples:', err);
    //     }
    // };

    return (
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Biochar Lab Samples (C_ORG & H:C)</Typography>
            <TableContainer component={Paper} sx={{ height: 'auto' }}>
                <Table aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">ProjectId</TableCell>
                            <TableCell align="left">BatchId</TableCell>
                            <TableCell align="left">KilnId</TableCell>
                            <TableCell align="left">Sample</TableCell>
                            <TableCell align="left">Sample Date</TableCell>
                            <TableCell align="left">Sample type (IPS or RS)</TableCell>
                            <TableCell align="left">H:Corg - Test value</TableCell>
                            <TableCell align="left">Organic C Content (OCb) - Test value</TableCell>
                            <TableCell align="left">Upload Lab Report</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {biocharLabSamplesState?.map((row, i) => {
                            const isString = typeof row.lab_report === 'string' && row.lab_report !== 'null';
                            return (
                                <TableRow key={i}>
                                    <TableCell align="left">{row.projectId}</TableCell>
                                    <TableCell align="left">{row.batchId}</TableCell>
                                    <TableCell align="left">{row.kilnUsed || row.kilnId}</TableCell>
                                    <TableCell align="left">{row.sample_number}</TableCell>
                                    <TableCell align="left">
                                        <FormControl>
                                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                                <DatePicker
                                                    name="sample_date"
                                                    value={row.sample_date ? moment(row.sample_date, 'YYYY-MM-DD') : null}
                                                    onChange={date => handleDateSelection(date, row.rowId)}
                                                    disabled={row.disabled ?? false}
                                                    format="YYYY-MM-DD"
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Select
                                            name="sample_type"
                                            value={row.sample_type}
                                            disabled={row.disabled ?? false}
                                            fullWidth
                                            onChange={e => handleInputChange(row.rowId, 'sample_type', e.target.value)}
                                        >
                                            {sampleTypes.map(type => (
                                                <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="h_corg"
                                            value={row.h_corg}
                                            onChange={e => handleInputChange(row.rowId, 'h_corg', e.target.value)}
                                            type="number"
                                            disabled={row.disabled ?? false}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        <TextField
                                            name="organic_carbon_content"
                                            value={row.organic_carbon_content}
                                            onChange={e => handleInputChange(row.rowId, 'organic_carbon_content', e.target.value)}
                                            type="number"
                                            disabled={row.disabled ?? false}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        {isString ? (
                                            <a href={row.lab_report} target="_blank" rel="noopener noreferrer">
                                                {row.lab_report}
                                            </a>
                                        ) : (
                                            <TextField
                                                disabled={row.disabled ?? false}
                                                name="lab_report"
                                                type="file"
                                                inputProps={{ accept: '.pdf,.doc,.docx,.jpg,.png' }}
                                                onChange={e => handleSourceDoc(e, row.rowId)}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {error ? <Alert severity='error' sx={{ mb: 2 }}>{errorMsg}</Alert> : null}
            <Box sx={{ position: 'relative', pt: 2 }}>
                {loading ?
                    <CircularProgress
                        sx={{ ml: 6 }}>
                    </CircularProgress> :
                    <Button
                        variant='contained'
                        onClick={handleSubmitData}>
                        Submit Data
                    </Button>
                }
                {/* {!loading && <Button variant='contained' sx={{ height: 'auto', float: 'right' }} onClick={handleDownload}>Download Excel</Button>} */}
            </Box>
            <Box>
                {loading &&
                    <Alert
                        severity='info'
                        sx={{ my: 2 }}>
                        Please be patient during data submission as file uploads can take some time to complete
                    </Alert>
                }
            </Box>
        </Grid>
    )
}
